import { CheckCircleIcon } from '@components/Icons/CheckCircleIcon';
import { LockIcon } from '@components/Icons/LockIcon';
import { usePasswordValidation } from '@lib/hooks/auth/password-criteria';
import { Box } from '@mui/material';
import { userStore } from '@stores/User';
import { useState } from 'react';
import { useNavigate } from 'react-router';
import { AuthButton } from '../AuthButton';
import { AuthErrorMessage } from '../AuthErrorMessage';
import { AuthField } from '../AuthField';
import { BackToLogin } from '../BackToLogin';
import { PasswordValidationComponent } from '../PasswordValidation/PasswordValidationComponent';
import css from './PasswordResetForm.module.scss';

export const PasswordResetForm = ({ code }: { code: string }) => {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const passwordValidation = usePasswordValidation(password, confirmPassword);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [passwordChangeSuccess, setPasswordChangeSuccess] = useState(false);
  const navigate = useNavigate();

  const submit = () => {
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    if (passwordValidation.some((condition) => !condition.satisfied)) {
      setError('Please satisfy all the conditions for your account security.');
      return;
    }

    setLoading(true);
    setError(null);
    userStore
      .resetPassword(code, password)
      .then(() => setPasswordChangeSuccess(true))
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  };

  if (passwordChangeSuccess) {
    return (
      <Box className={css.passwordResetFormContainer}>
        <Box className={css.passwordResetFormBox}>
          <Box className={css.passwordResetFormIcon}>
            <CheckCircleIcon />
          </Box>
          <Box className={css.passwordResetHeaderText}>
            <h2>Password reset!</h2>
            <span>
              Your password has been successfully reset. Click below to start using StarZero.
            </span>
          </Box>
          <AuthButton onClick={() => navigate('/')}>Go to homepage</AuthButton>
        </Box>
      </Box>
    );
  }

  return (
    <Box className={css.passwordResetFormContainer}>
      <Box className={css.passwordResetFormBox}>
        <Box className={css.passwordResetFormIcon}>
          <LockIcon />
        </Box>
        <Box className={css.passwordResetHeaderText}>
          <h2>Set your new password</h2>
          <span>Your new password must be different from your previous passwords.</span>
        </Box>
        <form
          className={css.passwordResetFormFields}
          onSubmit={(e) => {
            e.preventDefault();
            submit();
          }}
        >
          <AuthField
            fullWidth
            label="Password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            showPassword={showPassword}
            onTogglePassword={() => setShowPassword(!showPassword)}
            autoComplete="new-password"
            autoFocus
          />
          <AuthField
            fullWidth
            label="Confirm password"
            type="password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            showPassword={showPassword}
            onTogglePassword={() => setShowPassword(!showPassword)}
            autoComplete="new-password"
          />
          {error && <AuthErrorMessage message={error} />}
          <PasswordValidationComponent passwordValidation={passwordValidation} />
          <AuthButton type="submit" disabled={loading}>
            {loading ? 'Resetting password' : 'Reset password'}
          </AuthButton>
          <BackToLogin />
        </form>
      </Box>
    </Box>
  );
};
